import { logPopup } from './popup-logger.js';
import { reportWidgetEvent } from '../analytics/report-widget-event.js';

export type ExitIntentSignal = {
  clientY: number;
  relatedTarget: EventTarget | null;
};

export type ExitIntentMoveSignal = {
  clientY: number;
  movementY: number;
  previousClientY: number;
  topZonePx: number;
};

/** Cursor left the document upward (browser chrome / tab bar). */
export function isExitIntentLeaveSignal(event: ExitIntentSignal): boolean {
  return event.clientY <= 12 && event.relatedTarget == null;
}

/**
 * Cursor moves upward into the top band from below it
 * (classic “about to close the tab” gesture).
 */
export function isExitIntentMoveSignal(event: ExitIntentMoveSignal): boolean {
  const movingUp =
    event.movementY < 0 || event.clientY < event.previousClientY - 2;
  return (
    event.clientY <= event.topZonePx &&
    event.previousClientY > event.topZonePx &&
    movingUp
  );
}

export type AttachExitIntentOptions = {
  /** Top viewport band in px — default 48 */
  topZonePx?: number;
  throttleMs?: number;
  /** Ignore spikes right after attach (ms) */
  minTimeOnPageMs?: number;
  /** Cursor must go this far below the top band before exit intent can fire */
  minEngagementBelowTopPx?: number;
};

/**
 * Listen for exit intent on the host document.
 * Uses upward mousemove in the top band + mouseout fallback.
 * Returns whether the popup was shown from `onIntent`.
 */
export function attachExitIntent(
  onIntent: () => boolean | void,
  options?: AttachExitIntentOptions,
): () => void {
  if (typeof document === 'undefined') return () => {};

  const topZonePx = options?.topZonePx ?? 48;
  const throttleMs = options?.throttleMs ?? 80;
  const minTimeOnPageMs = options?.minTimeOnPageMs ?? 800;
  const minEngagementBelowTopPx = options?.minEngagementBelowTopPx ?? 100;
  const pageEnteredAt = Date.now();
  let lastMoveAt = 0;
  let lastClientY: number | null = null;
  let maxClientY = 0;
  let hasPointerSample = false;
  let shown = false;

  const isEngaged = () =>
    hasPointerSample && maxClientY >= topZonePx + minEngagementBelowTopPx;

  const maybeFire = (reason: 'move' | 'leave') => {
    if (shown) return;
    if (!hasPointerSample) return;
    if (!isEngaged()) return;
    if (Date.now() - pageEnteredAt < minTimeOnPageMs) return;

    const didShow = onIntent() === true;
    if (didShow) {
      shown = true;
      logPopup('exit-intent', 'trigger fired', {
        reason,
        topZonePx,
        maxClientY,
        elapsedMs: Date.now() - pageEnteredAt,
      });
      reportWidgetEvent('exit_intent_triggered', {
        reason,
        max_client_y: maxClientY,
        elapsed_ms: Date.now() - pageEnteredAt,
        top_zone_px: topZonePx,
      });
    } else {
      logPopup(
        'exit-intent',
        'trigger ignored — popup not shown',
        { reason, topZonePx, maxClientY },
        'debug',
      );
      reportWidgetEvent('exit_intent_suppressed', {
        reason,
        max_client_y: maxClientY,
        elapsed_ms: Date.now() - pageEnteredAt,
        suppress_reason: 'popup_not_shown',
      });
    }
  };

  const onMouseMove = (event: MouseEvent) => {
    const now = Date.now();
    if (now - lastMoveAt < throttleMs) return;
    lastMoveAt = now;

    hasPointerSample = true;
    maxClientY = Math.max(maxClientY, event.clientY);

    const previousClientY = lastClientY ?? event.clientY;
    lastClientY = event.clientY;

    if (
      isExitIntentMoveSignal({
        clientY: event.clientY,
        movementY: event.movementY,
        previousClientY,
        topZonePx,
      })
    ) {
      maybeFire('move');
    }
  };

  const onMouseOut = (event: MouseEvent) => {
    if (!isEngaged()) return;
    if (
      isExitIntentLeaveSignal({
        clientY: event.clientY,
        relatedTarget: event.relatedTarget,
      })
    ) {
      maybeFire('leave');
    }
  };

  document.addEventListener('mousemove', onMouseMove, { passive: true });
  document.addEventListener('mouseout', onMouseOut);

  logPopup('exit-intent', 'listener attached', {
    topZonePx,
    minTimeOnPageMs,
    minEngagementBelowTopPx,
  });

  return () => {
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseout', onMouseOut);
    logPopup('exit-intent', 'listener detached');
  };
}
